import { Injectable } from '@angular/core';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Observable, BehaviorSubject, of, map, scheduled, asyncScheduler } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStatus = new BehaviorSubject<boolean | null>(null);
  public redirectUrl: string | null = null;
  private api = '/api';

  constructor(private http: HttpClient) {}

  setRedirectUrl(url: string) {
    this.redirectUrl = url;
  }

  clearRedirectUrl() {
    this.redirectUrl = null;
  }

  isAuthenticated(): Observable<boolean | null> {
    return this.authStatus.asObservable();
  }

  checkAuth(): Observable<boolean> {
    return this.http.get<{ authenticated: boolean }>('/api/auth/status', { withCredentials: true }).pipe(
      tap(response => this.authStatus.next(response.authenticated)),
      map(response => response.authenticated),
      catchError((err, caught) => {
        console.error('Error while checking auth status', err);
        console.error('Caught: ', caught);
        this.authStatus.next(false);
        return scheduled([false], asyncScheduler);
      })
    );
  }

  login(credentials: any): Observable<any> {
    return this.http.post<any>('/api/auth', credentials, { withCredentials: true, observe: 'response' })
      .pipe(
      tap((response) => {
        if (response.status === HttpStatusCode.Ok) {
          this.authStatus.next(true);
          this.redirectUrl = response.body.redirectUrl || null;
        } else {
          this.authStatus.next(false);
        }
      }),
      catchError((error) => {
        this.authStatus.next(false);
        return scheduled([error], asyncScheduler);
      })
    );
  }

  logout(): Observable<any> {
    return this.http.patch('/api/auth', {}, { withCredentials: true, observe: 'response' }).pipe(
      tap((response) => {
        if (response.status === HttpStatusCode.Ok) {
          this.authStatus.next(false);
        } else {
          this.authStatus.next(true);
        }
      }),
      catchError((error) => {
        console.error('Error while logging out:', error);
        this.authStatus.next(true);
        return scheduled([error], asyncScheduler);
      })
    );
  }

  checkPermission(
    resourceId: string
  ): Observable<any> {
    return this.http.get<any>('/api/permissions/check', {
      withCredentials: true,
      params: { resourceId },
    });
  }

  getAllPermissions(): Observable<any[]> {
    return this.http.get<any[]>('/api/permissions/currentUser', { withCredentials: true }).pipe(
      catchError((error) => {
        console.error('Error fetching permissions:', error);
        return of([]);
      })
    );
  }

  refresh(): Observable<void> {
    return this.http.post(`${this.api}/auth/refresh`, {}, { withCredentials: true })
      .pipe(map(() => void 0));
  }

  hardLogout(): void {
    // Opcjonalnie poinformuj backend
    this.http.post(`${this.api}/auth/logout`, {}, { withCredentials: true })
      .subscribe({ complete: () => { /* ignore */ } });

    window.location.href = '/login';
  }

}
